import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ledgerIdOf } from "./accountUser";

export default function LedgerCombo({ label, ledgers, groups = [], value, onChange, required, disabled, error, placeholder }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const [menuBox, setMenuBox] = useState(null);
  const boxRef = useRef(null);
  const menuRef = useRef(null);
  const labelOf = useCallback((group) => groups.find((item) => item.value === group)?.label || group || "Other", [groups]);
  const rankOf = useCallback((group) => {
    const index = groups.findIndex((item) => item.value === group);
    return index === -1 ? groups.length : index;
  }, [groups]);
  const selected = ledgers.find((row) => ledgerIdOf(row) === String(value || "")) || null;
  const options = useMemo(() => {
    const seen = new Set();
    const rows = ledgers.filter((row) => {
      const id = ledgerIdOf(row);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    const text = filtering ? query.trim().toLowerCase() : "";
    const matched = text
      ? rows.filter((row) => (
        row.name.toLowerCase().includes(text)
        || String(row.code || "").toLowerCase().includes(text)
        || labelOf(row.group).toLowerCase().includes(text)
      ))
      : rows;
    return [...matched].sort((a, b) => (
      rankOf(a.group) - rankOf(b.group)
      || labelOf(a.group).localeCompare(labelOf(b.group))
      || a.name.localeCompare(b.name)
    ));
  }, [ledgers, query, filtering, labelOf, rankOf]);
  const buckets = [];
  options.forEach((row) => {
    const title = labelOf(row.group);
    const bucket = buckets.find((item) => item.title === title);
    if (bucket) bucket.rows.push(row);
    else buckets.push({ title, rows: [row] });
  });

  useLayoutEffect(() => {
    if (!open) {
      setMenuBox(null);
      return undefined;
    }
    const place = () => {
      const node = boxRef.current;
      const field = node ? node.querySelector("input") : null;
      if (!field) return;
      const rect = field.getBoundingClientRect();
      // body { zoom: 80% } scales fixed coordinates again, so convert the field's
      // on-screen box back into the page's own pixels.
      const probe = document.createElement("div");
      probe.style.cssText = "position:fixed;left:0;top:0;width:100px;height:100px;visibility:hidden;pointer-events:none;";
      document.body.appendChild(probe);
      const origin = probe.getBoundingClientRect();
      probe.remove();
      const scale = origin.width / 100 || 1;
      const css = (visual) => (visual - origin.left) / scale;
      const cssY = (visual) => (visual - origin.top) / scale;
      const gap = 4;
      const spaceBelow = (window.innerHeight - rect.bottom) / scale;
      const spaceAbove = rect.top / scale;
      const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
      const room = Math.max(140, Math.min(360, (openUp ? spaceAbove : spaceBelow) - gap));
      const left = css(rect.left);
      const width = rect.width / scale;
      setMenuBox({
        left,
        width,
        maxHeight: room,
        top: openUp ? "auto" : cssY(rect.bottom) + gap,
        bottom: openUp ? (window.innerHeight - rect.top) / scale + gap : "auto",
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (boxRef.current && boxRef.current.contains(target)) return;
      if (menuRef.current && menuRef.current.contains(target)) return;
      setFiltering(false);
      setOpen(false);
    };
    document.addEventListener("pointerdown", close, true);
    return () => document.removeEventListener("pointerdown", close, true);
  }, [open]);

  const choose = (row) => {
    onChange(row ? ledgerIdOf(row) : "");
    setQuery("");
    setFiltering(false);
    setOpen(false);
  };
  // Keyboard: the ledger whose name was typed exactly (any case), else the first one listed.
  const exactMatch = () => {
    const text = query.trim().toLowerCase();
    return text ? options.find((row) => row.name.trim().toLowerCase() === text) || null : null;
  };

  return (
    <label className="tally-field">
      <span>{label}{required ? " *" : ""}</span>
      <div className={`tally-combo${error ? " invalid" : ""}${open ? " open" : ""}`} ref={boxRef}>
        <input
          className="form-control"
          disabled={disabled}
          value={open ? query : (selected?.name || "")}
          placeholder={placeholder || "Search ledger"}
          onFocus={(event) => {
            setOpen(true);
            setFiltering(false);
            setQuery(selected?.name || "");
            event.target.select();
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setFiltering(true);
            setOpen(true);
          }}
          onBlur={(event) => {
            const next = event.relatedTarget;
            if (next instanceof Node && ((menuRef.current && menuRef.current.contains(next)) || (boxRef.current && boxRef.current.contains(next)))) return;
            const typed = filtering ? exactMatch() : null;
            if (typed && ledgerIdOf(typed) !== String(value || "")) {
              choose(typed);
              return;
            }
            setOpen(false);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") setOpen(false);
            if (event.key === "Enter" && open && filtering) {
              event.preventDefault();
              const pick = exactMatch() || options[0];
              if (pick) choose(pick);
            }
          }}
        />
        <div className="tally-combo-tools">
          {value && !disabled ? (
            <button type="button" className="tally-combo-tool" aria-label="Clear" title="Clear"
              onMouseDown={(event) => { event.preventDefault(); choose(null); }}>
              <i className="bi bi-x-lg" />
            </button>
          ) : null}
          <button type="button" className="tally-combo-tool" aria-label={open ? "Close list" : "Open list"} title={open ? "Close" : "Open"}
            disabled={disabled}
            onMouseDown={(event) => {
              event.preventDefault();
              setOpen((current) => !current);
              setFiltering(false);
              setQuery(selected?.name || "");
            }}>
            <i className={open ? "bi bi-chevron-up" : "bi bi-chevron-down"} />
          </button>
        </div>
        {open && !disabled && menuBox && createPortal(
          <div
            ref={menuRef}
            className="tally-combo-menu tally-combo-menu-fixed"
            style={{
              position: "fixed",
              left: menuBox.left,
              width: menuBox.width,
              top: menuBox.top,
              bottom: menuBox.bottom,
              maxHeight: menuBox.maxHeight,
              zIndex: 5000,
            }}
          >
            {buckets.length === 0 && <div className="tally-combo-empty">No matching ledger</div>}
            {buckets.map((group) => (
              <div key={group.title}>
                <div className="tally-combo-group">{group.title}</div>
                {group.rows.map((row) => (
                  <button
                    type="button"
                    key={ledgerIdOf(row)}
                    className={`tally-combo-option${ledgerIdOf(row) === String(value || "") ? " active" : ""}`}
                    title={row.name}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      choose(row);
                    }}
                  >
                    {row.name}
                  </button>
                ))}
              </div>
            ))}
          </div>,
          document.body
        )}
      </div>
      {error ? <span className="accounts-hint tally-error">{error}</span> : null}
    </label>
  );
}
